import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import type { SearchNavigate } from "../../lib/list-page/use-list-page";

/**
 * URL state for a role view that S4's `/accommodation` index route mounts
 * with no props (`NocAccommodation`, `HotelAccommodation`). The view parses
 * the current search with its own lenient shape — S4 spreads the same shape
 * into the index route's `validateSearch`, so the keys survive — and writes
 * back with a relative navigate, keeping every other key.
 */
export function useViewSearch<S extends z.ZodRawShape>(shape: S): { search: z.infer<z.ZodObject<S>>; navigate: SearchNavigate<z.infer<z.ZodObject<S>>> } {
	type T = z.infer<z.ZodObject<S>>;
	const raw = useSearch({ strict: false }) as Record<string, unknown>;
	const schema = useMemo(() => z.object(shape), [shape]);
	const search = useMemo(() => {
		const parsed = schema.safeParse(raw);
		return (parsed.success ? parsed.data : {}) as T;
	}, [schema, raw]);
	const nav = useNavigate();
	const navigate = useCallback<SearchNavigate<T>>(
		(opts) =>
			nav({
				to: ".",
				// biome-ignore lint/suspicious/noExplicitAny: the index route's search is S4's union; this view only touches its own keys
				search: (prev: any) => ({ ...prev, ...opts.search(prev as T) }),
				replace: opts.replace,
			}),
		[nav],
	);
	return { search, navigate };
}

/** A URL param that falls back to "not set" when it does not parse. */
export const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);
